import React from "react";
import { motion } from "framer-motion";
import { Clock, Users, BarChart3 } from "lucide-react";

const HowItWorks = ({ handleOpenModal }) => {
  const waitlistUrl = "https://portal.selfsync.ai/sign-up";

  const steps = [
    {
      number: "01",
      icon: <Clock className="w-8 h-8 text-purple-600" />,
      title: "Send 30-minute Assessment",
      description: "Your candidates take our conversational AI assessment. No stress, no trick questions - just natural conversation that reveals personality insights.",
      time: "30 minutes"
    },
    {
      number: "02", 
      icon: <Users className="w-8 h-8 text-cyan-500" />,
      title: "AI Analyzes Psychology",
      description: "Our AI reads between the lines, analyzing behavioral patterns and personality traits with the precision of a team of psychologists.",
      time: "Instant"
    },
    {
      number: "03",
      icon: <BarChart3 className="w-8 h-8 text-green-500" />,
      title: "Get Hiring Insights",
      description: "Receive detailed personality reports, role compatibility scores, and clear hiring recommendations. Know exactly who to interview.",
      time: "Immediate"
    }
  ];

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="py-8 px-6 md:px-14 lg:px-20 bg-white"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.2 } },
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            How{" "}
            <span className="bg-gradient-to-r from-purple-600 to-cyan-500 text-transparent bg-clip-text">
              It Works
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Simple 3-step process to revolutionize your hiring decisions
          </p>
        </motion.div>

        {/* Steps */}
       <div className="relative">
  {/* Connecting Line */}
  <div className="hidden lg:block absolute top-1/2 left-1/2 w-full h-0.5 bg-gradient-to-r from-purple-200 via-cyan-200 to-green-200 transform -translate-x-1/2 -translate-y-1/2 -z-10"></div>

  <div className="grid lg:grid-cols-3 gap-8">
    {steps.map((step, index) => (
      <motion.div
        key={index}
        className="relative bg-white rounded-2xl p-8 max-md:p-4 shadow-lg border border-gray-100 text-center group hover:shadow-xl transition-all duration-300 flex flex-col h-full" // 🟢 Make card flex column and full height
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.2, duration: 0.6 }}
      >
        {/* Step Number Circle */}
        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
            {step.number}
          </div>
        </div>

        {/* Icon */}
        <div className="mt-6 mb-6 flex justify-center">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            {step.icon}
          </div>
        </div>

        {/* Content */}
        <h3 className="text-2xl font-bold text-gray-900 mb-4">
          {step.title}
        </h3>

        <p className="text-gray-600 leading-relaxed mb-6">
          {step.description}
        </p>

        {/* Time Badge at Bottom */}
        <div className="mt-auto inline-block bg-gradient-to-r from-purple-100 to-cyan-100 text-purple-700 font-bold px-4 py-2 rounded-full text-sm">
          {step.time}
        </div>
      </motion.div>
    ))}
  </div>
</div>


        {/* CTA Section */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <div className="bg-gradient-to-r from-purple-50 to-cyan-50 rounded-2xl p-8">
            <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-4">
             Get Started?
            </h3>
            <p className="text-gray-600 mb-8 text-sm md:text-lg">
              Transform your hiring process in the next 30 minutes
            </p>
            
            <motion.button
              className="custom-button text-white font-bold px-2.5 md:px-8 py-2 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg text-base md:text-xl transform hover:scale-105"
              onClick={() => window.open(waitlistUrl, "_blank")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Try Your First Assessment Free
            </motion.button>
            
            <div className="mt-6 flex justify-center items-center gap-8 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                ✓ No credit card required
              </span>
              <span className="flex items-center gap-2">
                ✓ Setup in 15 minutes
              </span>
              <span className="flex items-center gap-2">
                ✓ Instant results
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default HowItWorks; 